import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { SectionCard } from '@/components/section-card';
import { QueryBoundary } from '@/components/query-boundary';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  useApFollowers, useApProfile, useSaveApProfile, useAnnounceApProfile, isUnannounced,
  useApReplies, useSendReply, usePatchReply,
} from '@/lib/queries/fediverse';
import type { ApReplyRow } from '@/types/api';
import { safeHttpHref } from '@/lib/safe-url';
import { useConfirm } from '@/components/confirm';

export const Route = createFileRoute('/fediverse')({ component: FediversePage });

function FediversePage() {
  return (
    <>
      <PageHeader title="페디버스" description="@zerry@zerry.co.kr — 액터 프로필 · 팔로워" />
      <div className="mt-6 space-y-6">
        <ProfileCard />
        <FollowersCard />
        <RepliesCard />
      </div>
    </>
  );
}

function ProfileCard() {
  const profile = useApProfile();
  const save = useSaveApProfile();
  const announce = useAnnounceApProfile();
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');

  // eslint-disable-next-line react-hooks/set-state-in-effect -- 서버 프로필(query)로 편집 draft 시드 — SettingsPanel 과 동일 패턴
  useEffect(() => { if (profile.data) { setName(profile.data.name); setSummary(profile.data.summary); } }, [profile.data]);

  const unannounced = profile.data
    ? isUnannounced(profile.data.updated_at, profile.data.announced_at)
    : false;

  return (
    <QueryBoundary query={profile}>
      {(data) => (
        <SectionCard title="액터 프로필">
          <div className="space-y-4 max-w-xl">
            <div>
              <Label htmlFor="ap-name">이름</Label>
              <Input id="ap-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ap-summary">소개 (평문 — HTML 은 서버가 처리)</Label>
              <Input id="ap-summary" value={summary} maxLength={500} onChange={(e) => setSummary(e.target.value)} />
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => save.mutate({ name, summary }, {
                  onSuccess: () => toast.success('저장됨 — 팔로워에게 알리려면 발송을 누르세요'),
                  onError: (e) => toast.error(e instanceof Error ? e.message : '저장 실패'),
                })}
                disabled={save.isPending}
              >저장</Button>
              <Button
                variant="outline"
                onClick={() => announce.mutate(undefined, {
                  onSuccess: (r) => toast.success(`팔로워 ${r.followers}명에게 Update(Person) 발송됨`),
                  onError: (e) => toast.error(e instanceof Error ? e.message : '발송 실패'),
                })}
                disabled={announce.isPending}
              >팔로워 {data.followers}명에게 알리기</Button>
              {unannounced && <Badge variant="destructive">미발송 변경 있음</Badge>}
            </div>
            <p className="text-xs text-muted-foreground">
              마지막 발송: {data.announced_at ? new Date(data.announced_at).toLocaleString('ko-KR') : '없음'}
              {' · '}핸들·아바타는 여기서 못 바꿉니다 — 핸들은 팔로워가 끊기고, 아바타는 파일 해시 계약(코드) 경유.
            </p>
          </div>
        </SectionCard>
      )}
    </QueryBoundary>
  );
}

function FollowersCard() {
  const followers = useApFollowers();
  return (
    <QueryBoundary query={followers}>
      {(data) => (
        <SectionCard title={`팔로워 ${data.total}명`}>
          <ul className="divide-y divide-border">
            {data.items.map((f) => {
              const actorHref = safeHttpHref(f.actor_uri);
              return (
                <li key={f.actor_uri} className="flex items-center justify-between py-2 text-sm">
                  {actorHref ? (
                    <a href={actorHref} target="_blank" rel="noreferrer" className="font-mono hover:underline">
                      {f.handle ?? f.actor_uri}
                    </a>
                  ) : (
                    <span className="font-mono">{f.handle ?? f.actor_uri}</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {f.created_at ? new Date(f.created_at).toLocaleDateString('ko-KR') : ''}
                  </span>
                </li>
              );
            })}
            {data.items.length === 0 && <li className="py-2 text-sm text-muted-foreground">아직 없음</li>}
          </ul>
        </SectionCard>
      )}
    </QueryBoundary>
  );
}

/** 저장된 content 는 sanitize 된 HTML(멘션 앵커 등) — 태그만 걷어내고 안의 텍스트는 남긴다. */
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function RepliesCard() {
  const replies = useApReplies();
  const sendReply = useSendReply();
  const patchReply = usePatchReply();
  const confirm = useConfirm();
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  function startReply(id: string) {
    setReplyingId((cur) => (cur === id ? null : id));
    setDraft('');
  }

  function submitReply(row: ApReplyRow) {
    const content = draft.trim();
    if (!content) return;
    sendReply.mutate({ in_reply_to_id: row.id, content }, {
      onSuccess: (result) => {
        if (result.state === 'sending') {
          toast.warning('배달에 실패해 전송 대기로 보관했습니다. 목록에서 재전송할 수 있습니다.');
        } else {
          toast.success('답장을 보냈습니다');
        }
        setReplyingId(null);
        setDraft('');
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : '전송 실패'),
    });
  }

  function toggleHidden(row: ApReplyRow) {
    patchReply.mutate({ id: row.id, action: row.is_hidden ? 'unhide' : 'hide' }, {
      onSuccess: () => toast.success(row.is_hidden ? '다시 표시됨' : '숨김 처리됨'),
      onError: (e) => toast.error(e instanceof Error ? e.message : '처리 실패'),
    });
  }

  function redeliver(row: ApReplyRow) {
    patchReply.mutate({ id: row.id, action: 'redeliver' }, {
      onSuccess: () => toast.success('재전송 요청함'),
      onError: (e) => toast.error(e instanceof Error ? e.message : '재전송 실패'),
    });
  }

  async function deleteReply(row: ApReplyRow) {
    const ok = await confirm({
      title: '답글 삭제',
      description: row.direction === 'out'
        ? '이 답장을 삭제합니다. 페디버스(마스토돈 스레드)에서도 회수됩니다.'
        : '이 답글을 블로그에서 삭제합니다. 원격 원본에는 영향이 없습니다.',
      confirmLabel: '삭제',
      tone: 'destructive',
    });
    if (!ok) return;
    patchReply.mutate({ id: row.id, action: 'delete' }, {
      onSuccess: () => toast.success('삭제했습니다'),
      onError: (e) => toast.error(e instanceof Error ? e.message : '삭제 실패'),
    });
  }

  return (
    <QueryBoundary query={replies}>
      {(data) => (
        <SectionCard title={`답글 ${data.items.length}개`}>
          <ul className="divide-y divide-border">
            {data.items.map((r) => {
              const actorHref = safeHttpHref(r.actor_uri);
              return (
                <li key={r.id} className={`py-3 text-sm ${r.is_hidden ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground">{r.post_slug}</span>
                        {actorHref ? (
                          <a href={actorHref} target="_blank" rel="noreferrer" className="font-medium truncate hover:underline">
                            {r.handle ?? r.actor_uri}
                          </a>
                        ) : (
                          <span className="font-medium truncate">{r.handle ?? r.actor_uri}</span>
                        )}
                        {r.state === 'sending' && <Badge variant="warning">전송 대기</Badge>}
                        {r.state === 'sent' && <Badge variant="success">전송됨</Badge>}
                        {r.state === 'received' && <Badge variant="secondary">수신</Badge>}
                        {r.state === 'deleted' && <Badge variant="destructive">삭제됨</Badge>}
                        {r.is_hidden && <Badge variant="outline">숨김</Badge>}
                      </div>
                      <p className="mt-1 text-[13px] whitespace-pre-wrap">{stripHtml(r.content)}</p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      {r.state === 'sending' && (
                        <Button variant="outline" size="sm" onClick={() => redeliver(r)} disabled={patchReply.isPending}>
                          재전송
                        </Button>
                      )}
                      {r.direction === 'in' && r.state === 'received' && (
                        <Button variant="outline" size="sm" onClick={() => startReply(r.id)}>답장</Button>
                      )}
                      {r.state !== 'deleted' && (
                        <Button variant="ghost" size="sm" onClick={() => toggleHidden(r)} disabled={patchReply.isPending}>
                          {r.is_hidden ? '숨김 해제' : '숨기기'}
                        </Button>
                      )}
                      {r.state !== 'deleted' && (
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => deleteReply(r)} disabled={patchReply.isPending}>
                          삭제
                        </Button>
                      )}
                    </div>
                  </div>
                  {replyingId === r.id && (
                    <div className="mt-2 space-y-2">
                      <Textarea
                        value={draft}
                        maxLength={2000}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="답장 내용"
                      />
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => submitReply(r)} disabled={sendReply.isPending || !draft.trim()}>
                          전송
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setReplyingId(null)}>취소</Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
            {data.items.length === 0 && <li className="py-2 text-sm text-muted-foreground">아직 없음</li>}
          </ul>
        </SectionCard>
      )}
    </QueryBoundary>
  );
}
